<script setup>
import { computed, onMounted, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import cortexSettingsAPI from 'dashboard/api/cortexSettings';
import NextButton from 'dashboard/components-next/button/Button.vue';
import NextCheckbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import AdaptiveSelect from 'dashboard/components-next/adaptive-select/AdaptiveSelect.vue';
import TagMultiSelectComboBox from 'dashboard/components-next/combobox/TagMultiSelectComboBox.vue';
import NextInput from 'dashboard/components-next/input/Input.vue';
import WithLabel from 'v3/components/Form/WithLabel.vue';
import SectionLayout from '../../settings/account/components/SectionLayout.vue';

const { t } = useI18n();

const isLoading = ref(false);
const isSaving = ref(false);
const phoneticReplacementRows = ref([]);
const conversion = reactive({
  timeout_seconds: 120,
  max_file_size_mb: 100,
  ocr_languages: 'por+eng',
  profile_selection_mode: 'automatic_with_review',
  default_profile: 'general_content',
  classification_confidence_threshold: 0.8,
  review_mode: 'partial',
  multimodal_fallback_enabled: true,
  canonical_language: 'pt_BR',
  raw_content_retention_days: 90,
  media_monthly_cost_limit_usd: 10,
});

const numericFields = [
  'timeout_seconds',
  'max_file_size_mb',
  'classification_confidence_threshold',
  'raw_content_retention_days',
  'media_monthly_cost_limit_usd',
];
const ocrLocaleByLanguageCode = {
  eng: 'en',
  por: 'pt_BR',
  deu: 'de',
  spa: 'es',
  fra: 'fr',
  ita: 'it',
};
const ocrLanguageOptions = computed(() => [
  {
    value: 'en',
    label: t(
      'GENERAL_SETTINGS.FORM.CORTEX_CONVERSION.OCR_LANGUAGES.OPTIONS.en'
    ),
  },
  {
    value: 'pt_BR',
    label: t(
      'GENERAL_SETTINGS.FORM.CORTEX_CONVERSION.OCR_LANGUAGES.OPTIONS.pt_BR'
    ),
  },
  {
    value: 'de',
    label: t(
      'GENERAL_SETTINGS.FORM.CORTEX_CONVERSION.OCR_LANGUAGES.OPTIONS.de'
    ),
  },
  {
    value: 'es',
    label: t(
      'GENERAL_SETTINGS.FORM.CORTEX_CONVERSION.OCR_LANGUAGES.OPTIONS.es'
    ),
  },
  {
    value: 'fr',
    label: t(
      'GENERAL_SETTINGS.FORM.CORTEX_CONVERSION.OCR_LANGUAGES.OPTIONS.fr'
    ),
  },
  {
    value: 'it',
    label: t(
      'GENERAL_SETTINGS.FORM.CORTEX_CONVERSION.OCR_LANGUAGES.OPTIONS.it'
    ),
  },
  {
    value: 'pt',
    label: t(
      'GENERAL_SETTINGS.FORM.CORTEX_CONVERSION.OCR_LANGUAGES.OPTIONS.pt'
    ),
  },
]);
const profileOptions = computed(() => [
  {
    value: 'technical_document',
    label: t('CORTEX.MEDIA.PROFILES.TECHNICAL_DOCUMENT'),
  },
  {
    value: 'support_screenshot',
    label: t('CORTEX.MEDIA.PROFILES.SUPPORT_SCREENSHOT'),
  },
  {
    value: 'receipt_or_form',
    label: t('CORTEX.MEDIA.PROFILES.RECEIPT_OR_FORM'),
  },
  { value: 'support_audio', label: t('CORTEX.MEDIA.PROFILES.SUPPORT_AUDIO') },
  {
    value: 'demonstration_video',
    label: t('CORTEX.MEDIA.PROFILES.DEMONSTRATION_VIDEO'),
  },
  {
    value: 'general_content',
    label: t('CORTEX.MEDIA.PROFILES.GENERAL_CONTENT'),
  },
]);
const profileSelectionModeOptions = computed(() => [
  {
    value: 'automatic',
    label: t('CORTEX.MEDIA.SETTINGS.SELECTION_AUTOMATIC'),
  },
  {
    value: 'user_defined',
    label: t('CORTEX.MEDIA.SETTINGS.SELECTION_USER'),
  },
  {
    value: 'automatic_with_review',
    label: t('CORTEX.MEDIA.SETTINGS.SELECTION_REVIEW'),
  },
]);
const reviewModeOptions = computed(() => [
  {
    value: 'automatic',
    label: t('CORTEX.PROCESSING.FORM.REVIEW_MODE_OPTIONS.AUTOMATIC'),
  },
  {
    value: 'partial',
    label: t('CORTEX.PROCESSING.FORM.REVIEW_MODE_OPTIONS.PARTIAL'),
  },
  {
    value: 'full',
    label: t('CORTEX.PROCESSING.FORM.REVIEW_MODE_OPTIONS.FULL'),
  },
]);

const parseOcrLanguages = value =>
  String(value || '')
    .split(/[,+\s]/)
    .filter(Boolean)
    .map(code => ocrLocaleByLanguageCode[code] || code);

const serializeOcrLanguages = locales =>
  [...new Set(locales)].filter(Boolean).join('+');

const selectedOcrLanguages = computed({
  get: () => parseOcrLanguages(conversion.ocr_languages),
  set: locales => {
    conversion.ocr_languages = serializeOcrLanguages(locales);
  },
});
const usesAutomaticProfile = computed(
  () => conversion.profile_selection_mode !== 'user_defined'
);
const alwaysReviewsProfile = computed(
  () => conversion.profile_selection_mode === 'automatic_with_review'
);

const applyConversionSettings = settings => {
  Object.assign(conversion, settings || {});
  phoneticReplacementRows.value = Object.entries(
    settings?.phonetic_replacements || {}
  ).map(([heard, expected]) => ({ heard, expected }));
};

const addPhoneticReplacement = () => {
  phoneticReplacementRows.value.push({ heard: '', expected: '' });
};

const removePhoneticReplacement = index => {
  phoneticReplacementRows.value.splice(index, 1);
};

const phoneticReplacementsPayload = () =>
  Object.fromEntries(
    phoneticReplacementRows.value
      .map(({ heard, expected }) => [heard.trim(), expected.trim()])
      .filter(([heard, expected]) => heard && expected)
  );

const fetchSettings = async () => {
  isLoading.value = true;
  try {
    const { data } = await cortexSettingsAPI.get();
    applyConversionSettings(data.conversion);
  } catch (error) {
    useAlert(t('GENERAL_SETTINGS.FORM.CORTEX_CONVERSION.API.ERROR'));
  } finally {
    isLoading.value = false;
  }
};

const saveSettings = async () => {
  isSaving.value = true;
  try {
    const payload = {
      conversion: {
        ...conversion,
        phonetic_replacements: phoneticReplacementsPayload(),
        ...Object.fromEntries(
          numericFields.map(field => [field, Number(conversion[field])])
        ),
      },
    };
    const { data } = await cortexSettingsAPI.update(payload);
    applyConversionSettings(data.conversion);
    useAlert(t('GENERAL_SETTINGS.FORM.CORTEX_CONVERSION.API.SUCCESS'));
  } catch (error) {
    useAlert(t('GENERAL_SETTINGS.FORM.CORTEX_CONVERSION.API.ERROR'));
  } finally {
    isSaving.value = false;
  }
};

onMounted(fetchSettings);
</script>

<template>
  <SectionLayout
    :title="t('GENERAL_SETTINGS.FORM.CORTEX_CONVERSION.TITLE')"
    :description="t('GENERAL_SETTINGS.FORM.CORTEX_CONVERSION.NOTE')"
    with-border
  >
    <woot-loading-state v-if="isLoading" />
    <form v-else class="grid gap-4" @submit.prevent="saveSettings">
      <div class="grid gap-4 md:grid-cols-2">
        <WithLabel
          name="cortex-conversion-timeout"
          :label="t('GENERAL_SETTINGS.FORM.CORTEX_CONVERSION.TIMEOUT.LABEL')"
          :help-message="
            t('GENERAL_SETTINGS.FORM.CORTEX_CONVERSION.TIMEOUT.HELP')
          "
        >
          <NextInput
            v-model="conversion.timeout_seconds"
            type="number"
            min="1"
            class="w-full"
            :placeholder="
              t('GENERAL_SETTINGS.FORM.CORTEX_CONVERSION.TIMEOUT.PLACEHOLDER')
            "
          />
        </WithLabel>
        <WithLabel
          name="cortex-conversion-max-file-size"
          :label="
            t('GENERAL_SETTINGS.FORM.CORTEX_CONVERSION.MAX_FILE_SIZE.LABEL')
          "
          :help-message="
            t('GENERAL_SETTINGS.FORM.CORTEX_CONVERSION.MAX_FILE_SIZE.HELP')
          "
        >
          <NextInput
            v-model="conversion.max_file_size_mb"
            type="number"
            min="1"
            class="w-full"
            :placeholder="
              t(
                'GENERAL_SETTINGS.FORM.CORTEX_CONVERSION.MAX_FILE_SIZE.PLACEHOLDER'
              )
            "
          />
        </WithLabel>
        <WithLabel
          name="cortex-ocr-languages"
          :label="
            t('GENERAL_SETTINGS.FORM.CORTEX_CONVERSION.OCR_LANGUAGES.LABEL')
          "
          :help-message="
            t('GENERAL_SETTINGS.FORM.CORTEX_CONVERSION.OCR_LANGUAGES.HELP')
          "
        >
          <TagMultiSelectComboBox
            v-model="selectedOcrLanguages"
            :options="ocrLanguageOptions"
            :placeholder="
              t(
                'GENERAL_SETTINGS.FORM.CORTEX_CONVERSION.OCR_LANGUAGES.PLACEHOLDER'
              )
            "
            :search-placeholder="
              t(
                'GENERAL_SETTINGS.FORM.CORTEX_CONVERSION.OCR_LANGUAGES.SEARCH_PLACEHOLDER'
              )
            "
            :empty-state="
              t('GENERAL_SETTINGS.FORM.CORTEX_CONVERSION.OCR_LANGUAGES.EMPTY')
            "
          />
        </WithLabel>
        <WithLabel
          name="cortex-profile-selection-mode"
          :label="t('CORTEX.MEDIA.SETTINGS.SELECTION_MODE')"
          :help-message="t('CORTEX.MEDIA.SETTINGS.SELECTION_MODE_HELP')"
        >
          <AdaptiveSelect
            v-model="conversion.profile_selection_mode"
            :options="profileSelectionModeOptions"
          />
        </WithLabel>
        <WithLabel
          v-if="!usesAutomaticProfile"
          name="cortex-default-profile"
          :label="t('CORTEX.MEDIA.SETTINGS.DEFAULT_PROFILE')"
          :help-message="t('CORTEX.MEDIA.SETTINGS.DEFAULT_PROFILE_HELP')"
        >
          <AdaptiveSelect
            v-model="conversion.default_profile"
            :options="profileOptions"
          />
        </WithLabel>
        <WithLabel
          v-if="usesAutomaticProfile"
          name="cortex-classification-confidence"
          :label="t('CORTEX.MEDIA.SETTINGS.CONFIDENCE')"
          :help-message="t('CORTEX.MEDIA.SETTINGS.CONFIDENCE_HELP')"
        >
          <NextInput
            v-model="conversion.classification_confidence_threshold"
            type="number"
            min="0"
            max="1"
            step="0.05"
            class="w-full"
          />
        </WithLabel>
        <WithLabel
          v-if="!alwaysReviewsProfile"
          name="cortex-media-review-mode"
          :label="t('CORTEX.MEDIA.SETTINGS.REVIEW_MODE')"
          :help-message="t('CORTEX.MEDIA.SETTINGS.REVIEW_MODE_HELP')"
        >
          <AdaptiveSelect
            v-model="conversion.review_mode"
            :options="reviewModeOptions"
          />
        </WithLabel>
        <WithLabel
          name="cortex-raw-content-retention"
          :label="t('CORTEX.MEDIA.SETTINGS.RETENTION')"
          :help-message="t('CORTEX.MEDIA.SETTINGS.RETENTION_HELP')"
        >
          <NextInput
            v-model="conversion.raw_content_retention_days"
            type="number"
            min="1"
            class="w-full"
          />
        </WithLabel>
        <WithLabel
          name="cortex-canonical-language"
          :label="t('CORTEX.MEDIA.SETTINGS.CANONICAL_LANGUAGE')"
          :help-message="t('CORTEX.MEDIA.SETTINGS.CANONICAL_LANGUAGE_HELP')"
        >
          <AdaptiveSelect
            v-model="conversion.canonical_language"
            :options="ocrLanguageOptions"
          />
        </WithLabel>
        <WithLabel
          name="cortex-media-cost-limit"
          :label="t('CORTEX.MEDIA.SETTINGS.COST_LIMIT')"
          :help-message="t('CORTEX.MEDIA.SETTINGS.COST_LIMIT_HELP')"
        >
          <NextInput
            v-model="conversion.media_monthly_cost_limit_usd"
            type="number"
            min="0"
            step="0.5"
            class="w-full"
          />
        </WithLabel>
        <label
          v-if="usesAutomaticProfile"
          class="flex items-start gap-3 text-sm text-n-slate-12"
        >
          <NextCheckbox
            v-model="conversion.multimodal_fallback_enabled"
            class="mt-0.5 shrink-0"
          />
          <span>
            <span class="block font-medium">
              {{ t('CORTEX.MEDIA.SETTINGS.MULTIMODAL') }}
            </span>
            <span class="block text-label-small leading-4 text-n-slate-11">
              {{ t('CORTEX.MEDIA.SETTINGS.MULTIMODAL_HELP') }}
            </span>
          </span>
        </label>
      </div>
      <div class="grid gap-3 border-t border-n-weak pt-4">
        <div>
          <h3 class="text-sm font-medium text-n-slate-12">
            {{ t('CORTEX.MEDIA.SETTINGS.PHONETIC_REPLACEMENTS.TITLE') }}
          </h3>
          <p class="mt-1 text-xs text-n-slate-11">
            {{ t('CORTEX.MEDIA.SETTINGS.PHONETIC_REPLACEMENTS.HELP') }}
          </p>
        </div>
        <div
          v-for="(replacement, index) in phoneticReplacementRows"
          :key="index"
          class="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)_auto] items-center gap-2"
        >
          <NextInput
            v-model="replacement.heard"
            :placeholder="
              t('CORTEX.MEDIA.SETTINGS.PHONETIC_REPLACEMENTS.HEARD')
            "
          />
          <span class="i-lucide-arrow-right size-4 text-n-slate-10" />
          <NextInput
            v-model="replacement.expected"
            :placeholder="
              t('CORTEX.MEDIA.SETTINGS.PHONETIC_REPLACEMENTS.EXPECTED')
            "
          />
          <NextButton
            type="button"
            ghost
            ruby
            sm
            icon="i-lucide-trash-2"
            :aria-label="
              t('CORTEX.MEDIA.SETTINGS.PHONETIC_REPLACEMENTS.REMOVE')
            "
            @click="removePhoneticReplacement(index)"
          />
        </div>
        <NextButton
          type="button"
          outline
          sm
          icon="i-lucide-plus"
          :label="t('CORTEX.MEDIA.SETTINGS.PHONETIC_REPLACEMENTS.ADD')"
          class="justify-self-start"
          @click="addPhoneticReplacement"
        />
      </div>
      <NextButton
        type="submit"
        blue
        icon="i-lucide-save"
        :is-loading="isSaving"
      >
        {{ t('GENERAL_SETTINGS.FORM.CORTEX_CONVERSION.SUBMIT') }}
      </NextButton>
    </form>
  </SectionLayout>
</template>
